<template>
  <v-lightbox
    :key="randomKey"
    maxed
    is-authenticated
    :class="parentClass"
    head-class="px-4"
    content-class="px-4"
    foot-class="px-4"
  >
    <template v-slot:trigger>
      <slot name="lightbox-trigger">
        <!-- Custom slot to trigger <v-lightbox> -->
      </slot>
    </template>

    <template v-slot:head>
      <div class="flex items-center">
        <v-icon icon="loupe" size="5" class="mr-2"></v-icon> Iskanje
      </div>
    </template>

    <template v-slot:content>
      <div class="space-y-4">
        <v-main-search-dropdown
          :placeholder="countryPlaceholder.label"
          icon="map-pin"
          ref="countryDropdown"
          :show="false"
          class="rounded-full bg-white text-sm font-medium text-black-78 shadow"
          wrapper-classes="px-8"
        >
          <template v-slot:options>
            <ul class="text-sm font-medium">
              <li>
                <a
                  href="/drzava/svn"
                  class="block overflow-hidden overflow-ellipsis rounded-xl px-2.5 py-2.5 leading-none text-black-78 hover:bg-green-10"
                >
                  Slovenija
                </a>
              </li>
              <li>
                <a
                  href="/drzava/hrv"
                  class="block overflow-hidden overflow-ellipsis rounded-xl px-2.5 py-2.5 leading-none text-black-78 hover:bg-green-10"
                >
                  Hrvaška
                </a>
              </li>
            </ul>
          </template>
        </v-main-search-dropdown>

        <v-accordion
          :title="regionPlaceholder.region"
          icon="map-pin"
          ref="accordionRegion"
        >
          <template v-slot:content>
            <div class="px-4 pb-8">
              <div
                class="relative flex items-center rounded-full border border-gray bg-white"
                :class="{ 'border-salmon': regionError != null }"
              >
                <label
                  for="region"
                  class="absolute left-0 top-0 z-10 flex h-full w-full cursor-pointer items-center rounded-full px-8 py-4 font-medium text-black-50 hover:bg-green-10 md:px-10"
                  v-if="!hideRegionLabel && regionOption === null"
                  @click="openRegion"
                >
                  <div
                    class="overflow-hidden text-ellipsis whitespace-nowrap text-sm"
                  >
                    <span class="text-black-78">Regija</span>
                  </div>
                </label>
                <v-select
                  class="w-full font-medium"
                  :options="regions"
                  placeholder=""
                  v-model="regionOption"
                  label="region"
                  @open="onOpen"
                  @search:focus="onFocus('region')"
                  @search:blur="onBlur('region')"
                  @option:selected="onRegionSelect"
                  ref="region"
                  id="region"
                  :name="'region'"
                >
                  <!-- @option:selected="fetchLocationsFromRegion" -->

                  <template v-slot:no-options>
                    Za ta iskalni niz ni zadetkov.
                  </template>
                </v-select>
                <input
                  type="hidden"
                  name="region"
                  :value="regionOption == null ? '' : regionOption.region"
                />

                <div
                  v-if="regionOption != null"
                  class="absolute right-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer text-black-78"
                >
                  <v-icon
                    icon="close"
                    size="5"
                    @click="regionOption = null"
                  ></v-icon>
                </div>
              </div>
              <p
                v-if="regionError != null"
                v-text="regionError"
                class="mt-2 text-sm text-salmon"
              ></p>
            </div>
          </template>
        </v-accordion>

        <v-accordion
          :title="locationPlaceholder.custom_label"
          icon="map-pin"
          ref="accordionLocation"
        >
          <template v-slot:content>
            <div class="px-4 pb-8">
              <div
                class="relative flex items-center rounded-full border border-gray bg-white"
                :class="{ 'border-salmon': locationError != null }"
              >
                <label
                  for="location"
                  class="absolute left-0 top-0 z-10 flex h-full w-full cursor-pointer items-center rounded-full px-8 py-4 font-medium text-black-50 hover:bg-green-10 md:px-10"
                  v-if="!hideLocationLabel && locationOption == null"
                  @click="openLocation"
                >
                  <div
                    class="overflow-hidden text-ellipsis whitespace-nowrap text-sm"
                  >
                    <span class="text-black-78">Lokacija:</span>
                    mesto, ulica, poštna št.
                  </div>
                </label>
                <v-select
                  class="w-full font-medium text-black-78"
                  :options="locationsData"
                  placeholder=""
                  v-model="locationOption"
                  label="custom_label"
                  @search:focus="onFocus('location')"
                  @search:blur="onBlur('location')"
                  @search="fetchLocations"
                  @option:selected="onLocationSelect"
                  ref="location"
                  id="location"
                >
                  <template v-slot:no-options>
                    Za ta iskalni niz ni zadetkov.
                  </template>
                </v-select>
                <input
                  type="hidden"
                  name="location"
                  :value="locationOption == null ? '' : locationOption.value"
                />

                <div
                  v-if="locationOption != null"
                  class="absolute right-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer text-black-78"
                >
                  <v-icon
                    icon="close"
                    size="5"
                    @click="locationOption = null"
                  ></v-icon>
                </div>
              </div>
              <p
                v-if="locationError != null"
                v-text="locationError"
                class="mt-2 text-sm text-salmon"
              ></p>
            </div>
          </template>
        </v-accordion>

        <v-accordion
          :title="rentOrBuyPlaceholder.label"
          :icon="rentOrBuyPlaceholder.icon"
          ref="accordionRentOrBuy"
        >
          <template v-slot:content>
            <div class="px-4 pb-8">
              <div class="grid grid-cols-2 gap-5">
                <div v-for="(option, index) in rentOrBuyOptions">
                  <v-form-radio
                    name="rent_or_buy_searchlightbox"
                    :id="'rent_or_buy_searchlightbox_' + index"
                    :label="option.label"
                    :value="option.value"
                    v-model="rentOrBuyValue"
                    :fixed-height="true"
                  >
                    <template v-slot:icon>
                      <v-icon
                        :icon="option.icon"
                        size="5"
                        class="mr-2"
                      ></v-icon>
                    </template>
                  </v-form-radio>
                </div>
              </div>
            </div>
          </template>
        </v-accordion>

        <v-accordion
          :title="realestateTypePlaceholder.label"
          :icon="realestateTypePlaceholder.icon"
          ref="accordionRealestateType"
        >
          <template v-slot:content>
            <div class="px-4 pb-8">
              <div class="grid grid-cols-2 gap-5">
                <div v-for="(option, index) in realestateTypeOptions">
                  <v-form-radio
                    name="realestate_property_type_searchlightbox"
                    :id="'realestate_property_type_searchlightbox_' + index"
                    :label="option.label"
                    :value="option.value"
                    v-model="realestateTypeValue"
                    :fixed-height="true"
                  >
                    <template v-slot:icon>
                      <v-icon
                        :icon="option.icon"
                        size="5"
                        class="mr-2"
                      ></v-icon>
                    </template>
                  </v-form-radio>
                </div>
              </div>
            </div>
          </template>
        </v-accordion>

        <v-accordion
          :title="pricePlaceholder.label"
          icon="euro"
          ref="accordionPrice"
        >
          <template v-slot:content>
            <div class="px-4 pb-8">
              <div class="flex items-center gap-3">
                <div>
                  <v-form-input
                    id="price_from_searchlightbox"
                    label="od"
                    type="number"
                    :value="minPrice"
                    v-model="minPrice"
                    append-text="€"
                  ></v-form-input>
                </div>

                <div>
                  <v-form-input
                    id="price_to_searchlightbox"
                    label="do"
                    type="number"
                    :value="maxPrice"
                    v-model="maxPrice"
                    append-text="€"
                  ></v-form-input>
                </div>
              </div>

              <div class="mt-5 flex justify-center gap-5">
                <!-- -->

                <button class="button" @click="confirmPrice">
                  <v-icon icon="check"></v-icon>
                  Potrdi
                </button>
              </div>
            </div>
          </template>
        </v-accordion>
      </div>
    </template>
    <template v-slot:foot>
      <div class="flex w-full gap-3 px-4">
        <div class="w-1/2">
          <button
            class="button button--gray w-full px-2"
            @click="resetSearchCriteria"
          >
            <v-icon icon="reset"></v-icon>
            Ponastavi
          </button>
        </div>

        <div class="w-1/2">
          <form :action="this.$route('web.search')" id="searchForm">
            <input type="hidden" name="region" :value="regionOption?.region" />
            <input
              type="hidden"
              name="location"
              :value="locationOption?.value"
            />
            <input type="hidden" name="rent_or_buy" :value="rentOrBuyValue" />
            <input
              type="hidden"
              name="realestate_property_type"
              :value="realestateTypeValue"
            />
            <input type="hidden" name="price_from" :value="minPrice" />
            <input type="hidden" name="price_to" :value="maxPrice" />
            <button
              type="submit"
              class="button w-full px-2"
              @click.prevent="submitForm"
            >
              <v-icon icon="loupe"></v-icon>
              Iščite
            </button>
          </form>
        </div>
      </div>
    </template>
  </v-lightbox>
</template>

<script>
import debounce from "lodash/debounce";
import MainSearchDropdown from "../MainSearch/MainSearchDropdown.vue";

export default {
  name: "SearchLightbox",

  components: {
    "v-main-search-dropdown": MainSearchDropdown,
  },

  props: {
    query: {
      type: Object,
      default: {},
    },
    location: {
      type: String,
      default: null,
    },
    rentOrBuy: {
      type: String,
      default: null,
    },
    propertyType: {
      type: String,
      default: null,
    },
    priceFrom: {
      type: [String, Number],
      default: null,
    },
    priceTo: {
      type: [String, Number],
      default: null,
    },
    i18n: {
      type: Object,
      required: true,
    },
    unparsedEnums: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
      default: {},
    },
    parentClass: {
      type: String,
      default: "",
    },
    currentCountry: {
      type: String,
      required: true,
      default: "",
    },
  },

  data() {
    return {
      randomKey: Math.floor(Math.random() * 1000),
      openedAccordion: null,
      locationOption: null,
      locationError: null,
      regionError: null,
      hideLocationLabel: false,
      rentOrBuyValue: "kauf",
      realestateTypeValue: "",
      minPrice: "",
      maxPrice: "",
      regions: [],
      regionOption: null,
      hideRegionLabel: false,
      locationsData: [],
    };
  },
  created() {
    // This is a fix to hide modal on back press on mobile
    // It forces the rerender, thus reseting the lightbox
    window.addEventListener("pageshow", this.rerender);
  },
  beforeUnmount() {
    // This is a fix to hide modal on back press on mobile
    // It forces the rerender, thus reseting the lightbox
    window.removeEventListener("pageshow", this.rerender);
  },
  mounted() {
    (async () => {
      // Fetch locations and wait for function to finish and then continue
      await this.fetchLocations(this.location, "mounted");
    })();

    if (this.query.region) {
      this.regionOption = {
        region: this.query.region,
      };
    } else {
      this.regionOption = null;
    }

    this.minPrice = this.query.price_from;
    this.maxPrice = this.query.price_to;

    this.rentOrBuyValue =
      this.rentOrBuyOptions.find(
        (option) => option.value === this.query.rent_or_buy
      )?.value ?? "kauf";

    this.realestateTypeValue =
      this.realestateTypeOptions.find(
        (option) => option.value === this.query.realestate_property_type
      )?.value ?? "wohnung";

    this.fetchRegions();
  },
  watch: {
    locationOption: {
      handler: function (val, oldVal) {
        if (val !== null) {
          this.locationError = null;
          this.toggleAccordion("accordionLocation");
          this.toggleAccordion("accordionRentOrBuy");
        }
      },
    },
    rentOrBuyValue: {
      handler: function (val, oldVal) {
        if (val !== "") {
          this.toggleAccordion("accordionRentOrBuy");
          this.toggleAccordion("accordionRealestateType");
        }
      },
    },
    realestateTypeValue: {
      handler: function (val, oldVal) {
        if (val !== "") {
          this.toggleAccordion("accordionRealestateType");
          this.toggleAccordion("accordionPrice");
        }
      },
    },
  },
  computed: {
    rentOrBuyOptions() {
      return this.parseEnums("vermarktungsart");
    },

    rentOrBuyPlaceholder() {
      this.$refs.rentOrBuyDropdown
        ? (this.$refs.rentOrBuyDropdown.show = false)
        : null;

      return this.rentOrBuyOptions.find(
        (option) => option.value === this.rentOrBuyValue
      );
    },

    realestateTypeOptions() {
      return this.parseEnums("objektart");
    },

    realestateTypePlaceholder() {
      this.$refs.realestateTypeDropdown
        ? (this.$refs.realestateTypeDropdown.show = false)
        : null;

      if (this.realestateTypeValue === "") {
        return {
          label: "Tip nepremičnine",
          icon: "apartment",
        };
      }

      return this.realestateTypeOptions.find(
        (option) => option.value === this.realestateTypeValue
      );
    },

    locationPlaceholder() {
      if (this.locationOption == null) {
        return {
          custom_label: "Lokacija",
        };
      }

      return this.locationOption;
    },
    rentOrBuyPlaceholder() {
      if (this.rentOrBuyValue == null) {
        return {
          label: "Prodaja ali oddaja",
          icon: "key",
        };
      }

      return this.rentOrBuyOptions.find(
        (option) => option.value === this.rentOrBuyValue
      );
    },
    realestateTypePlaceholder() {
      if (this.realestateTypeValue == null) {
        return {
          label: "Tip nepremičnine",
          icon: "house",
        };
      }

      return this.realestateTypeOptions.find(
        (option) => option.value === this.realestateTypeValue
      );
    },
    pricePlaceholder() {
      if (this.minPrice && !this.maxPrice) {
        return {
          label: `${this.$filters.formatNumber(this.minPrice)} €`,
          icon: "euro",
        };
      }

      if (!this.minPrice && this.maxPrice) {
        return {
          label: `0 - ${this.$filters.formatNumber(this.maxPrice)} €`,
          icon: "euro",
        };
      }

      if (this.minPrice && this.maxPrice) {
        return {
          label: `${this.$filters.formatNumber(
            this.minPrice
          )} € - ${this.$filters.formatNumber(this.maxPrice)} €`,
          icon: "euro",
        };
      }

      return {
        label: "Cena",
        icon: "euro",
      };
    },

    countryPlaceholder() {
      return {
        label: this.currentCountry,
      };
    },

    regionPlaceholder() {
      if (this.regionOption === null) {
        return {
          region: "Regija",
        };
      }

      return this.regionOption;
    },
  },

  methods: {
    rerender() {
      // This is a fix to hide the modal on mobile when back button is pressed
      // It recreates the component, reseting the show on lightbox
      this.randomKey = Math.floor(Math.random() * 1000);
    },
    submitForm() {
      if (!this.locationOption && !this.regionOption) {
        if (!this.regionOption) {
          this.regionError = "Prosimo vnesite regijo";
          this.openAccordion("accordionRegion");
        } else if (!this.locationOption && !this.regionOption) {
          this.locationError = "Prosimo vnesite lokacijo";
          this.openAccordion("accordionLocation");
        }

        this.closeAccordion("accordionRentOrBuy");
        this.closeAccordion("accordionRealestateType");
        this.closeAccordion("accordionPrice");

        return;
      }

      document.getElementById("searchForm").submit();
    },
    onOpen() {},
    openLocation() {
      // Access the Vue Select instance using the ref
      const selectComponent = this.$refs.location;

      // Check if the Vue Select instance exists and is defined
      if (selectComponent) {
        selectComponent.$refs.search.focus();
      }
    },

    onLocationSelect(selectedLocation) {
      this.setRegion();
    },

    openRegion() {
      // Access the Vue Select instance using the ref
      const selectComponent = this.$refs.region;

      // Check if the Vue Select instance exists and is defined
      if (selectComponent) {
        selectComponent.$refs.search.focus();
      }
    },

    onRegionSelect() {
      this.clearLocations();
      this.openAccordion("accordionLocation");
      this.closeAccordion("accordionRegion");
    },

    onFocus(value) {
      if (value === "region") {
        this.hideRegionLabel = true;
      } else {
        this.hideLocationLabel = true;
      }
    },

    onBlur(value) {
      if (value === "region") {
        this.hideRegionLabel = false;
      } else {
        this.hideLocationLabel = false;
      }
    },

    resetSearchCriteria() {
      this.regionOption = null;
      this.locationOption = null;
      this.rentOrBuyValue = "";
      this.realestateTypeValue = "";
      this.minPrice = "";
      this.maxPrice = "";
    },

    confirmPrice() {
      this.toggleAccordion("accordionPrice");
    },

    toggleAccordion(ref) {
      this.$refs[ref]?.toggleAccordion();
    },

    openAccordion(ref) {
      this.$refs[ref]?.openAccordion();
    },

    closeAccordion(ref) {
      this.$refs[ref]?.closeAccordion();
    },

    clearLocations() {
      this.locationsData = [];
      this.locationOption = null;
    },

    setRegion() {
      this.regionOption = {
        region: this.locationOption.region,
      };
    },

    fetchRegions() {
      axios
        .get(this.$route("api.regions"), {params: {country: this.currentCountry}})
        .then((response) => {
          const regions = response.data.data;
          this.regions = regions;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    fetchLocations: debounce(async function (value, from = null) {
      if (value === "" || value === null) {
        this.locationsData = [];
        return;
      }

      try {
        const response = await axios.get(this.$route("api.locations"), {
          params: {
            region: this.regionOption?.region,
            search: value,
          },
        });
        let locations = response.data.data;
        this.locationsData = locations.map((location) => {
          return {
            value: location["code"],
            custom_label: location["location"],
            region: location["region"],
          };
        });
        // Set location option if query is present and on mounted only
        if (from === "mounted" && this.query.location) {
          this.locationOption = this.locationsData.find(
            (loc) => loc.value === this.query.location
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        // Ensure loading(false) is defined
        // this.loading(false);
      }
    }, 500),

    parseEnums(key) {
      let specificEnums = [];

      Object.values(this.unparsedEnums).forEach((enumGroup) => {
        if (enumGroup.group == key) {
          Object.keys(enumGroup.values).forEach((enumGroupValueKey) => {
            specificEnums.push({
              value: enumGroupValueKey,
              label: enumGroup.values[enumGroupValueKey],
              icon: this.i18n[enumGroupValueKey]
                ? this.i18n[enumGroupValueKey].icon
                : "",
            });
          });
        }
      });

      return specificEnums;
    },
  },
};
</script>
